import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createFavourite, deleteFavourite, selectFavouriteId } from '../reducers/favouritesReducer'

// Favourite state for a resource/term modal. The store is only loaded on the
// /favourites/ page, so fall back to the row id carried on the payload
// (`props.favourite`: row id, false, or null for anonymous). That payload id
// goes stale after a delete until the async resource refresh lands, so a row
// this hook has deleted is remembered in `removedId` and ignored.
const useFavourite = (props, typeKey) => {
    const dispatch = useDispatch()
    const storeId = useSelector(state => selectFavouriteId(state, props.content_type, props.id))
    const [removedId, setRemovedId] = useState(null)
    const favId = storeId ?? (props.favourite && props.favourite !== removedId ? props.favourite : null)
    const favourited = Boolean(favId)
    const inFlight = useRef(false)

    // Ignore toggles while a create/delete is pending: a double-click would
    // otherwise send a second POST, which the unique (user, content_type,
    // object_id) constraint now rejects with a 400 and a false error toast.
    // The handler stays synchronous (returns undefined) so callers and tests
    // using act(() => toggleFavourite()) are unaffected.
    const toggleFavourite = () => {
        if (inFlight.current) return
        inFlight.current = true
        // `favourited` and `favId` are this render's values, i.e. click time.
        const action = favourited
            ? deleteFavourite(favId, typeKey, props.id)
            : createFavourite({ object_id: props.id, content_type: props.content_type }, typeKey)
        // Release on either outcome; the thunks report their own errors.
        const release = () => { inFlight.current = false }
        // A re-favourite gets a new row id that lands in the store (storeId wins),
        // so remembering the deleted id never conflicts. Strict `=== true`: the
        // thunk reports success; anything else leaves the state untouched.
        Promise.resolve(dispatch(action)).then(result => {
            if (favourited && result === true) setRemovedId(favId)
            release()
        }, release)
    }

    return { favourited, toggleFavourite }
}

export default useFavourite
